import {fromJS, Set as ImmSet, OrderedMap} from 'immutable';
import _ from 'lodash';
import {cssPropFromPath} from '@packages/shared/LegacyStyleSystem/utils/CssPropertyUtils';
import {
  cssToPropertyList,
  cssToPropertyMap,
} from '@packages/systems/core/utils/CssStringUtils';
import type {
  StyleBlock,
  StyleBlockVariant,
} from '@packages/systems/style-legacy/records';
import {containsCssCustomValue} from '@packages/systems/style/raw-values';
import {sortCssProperties} from '@packages/systems/style-legacy/css-properties';
import {createStyleRuleGuid} from '@packages/shared/LegacyStyleSystem/utils/StyleRuleUtils';
import type {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import type {BreakpointID} from '@packages/systems/style/types';
import type {
  CustomNodeStateKey,
  PseudoStateKey,
} from '@packages/systems/core/utils/StyleTypes';
import {StylePath} from '@packages/systems/style-legacy/values';
import {createVariantKey} from '@packages/systems/style-legacy/variants';
import {getJustCssProperties} from '@packages/shared/LegacyStyleSystem/utils/StyleBlockUtils';
import type {Unit} from '@packages/systems/style-legacy/utils';

import {reifyStyleBlock} from './reify-style-block';
import {VariantStateKey} from '@packages/systems/style-legacy/selectors';
import {BREAKPOINT_ID_MAIN} from '@packages/systems/style/breakpoint-ids';

export type StyleUpdateSpec = {
  path: string;
  value: unknown;
  raw?: string;
  unit?: Unit;
};

/**
 * INTERNAL USE ONLY
 *
 * When pasting raw CSS, we need to ensure that the intended order of the CSS is
 * maintained.
 *
 * To accomplish this we use the `StyleUpdateSpec[]` from the original
 * `setStyles` call which should contain the original order from the pasted CSS.
 */
function ensureRawPasteOrder(
  oldLess: string,
  updateSpecFromRawPaste: StyleUpdateSpec[]
): string {
  const specIndexByProperty = new Map();
  for (let i = 0; i < updateSpecFromRawPaste.length; i += 1) {
    const expandedPath = StylePath.expandPath(updateSpecFromRawPaste[i]!.path);
    const propertyPathName = expandedPath[expandedPath.length - 1];
    if (!propertyPathName) throw new Error('Invalid expanded path');
    const propertyName = cssPropFromPath(propertyPathName);
    specIndexByProperty.set(propertyName, i);
  }

  // Shallow copy so we can call .sort without mutating original result, in case
  // this gets memoized:
  const propertyList: Array<{
    property: string;
    value: string;
  }> = [...cssToPropertyList(oldLess)];

  propertyList.sort((a, b): number => {
    const aSpecIndex = specIndexByProperty.get(a.property);
    const bSpecIndex = specIndexByProperty.get(b.property);

    // Sort pasted properties based on their original order:
    if (aSpecIndex != null && bSpecIndex != null)
      return aSpecIndex - bSpecIndex;

    // Push all properties being pasted after properties that aren't:
    if (aSpecIndex != null && bSpecIndex == null) return 1;
    if (aSpecIndex == null && bSpecIndex != null) return -1;

    return 0;
  });

  return propertyList
    .map(({property, value}) => `${property}: ${value};`)
    .join(' ');
}

/**
 * Updates specific properties of an inner variant's styleLess value.
 * @param  {StyleBlock}     styleBlock          The parent StyleBlock.
 * @param  {String}         options.variantKey  The path to the variant rule, e.g. "main", "small_hover", etc
 * @param  {Object|String}  options.newStyles   A) A map of discrete changes to apply on top of the existing variant styles
 *                                                * { 'margin-top': '20px', 'color': null } --> adds/sets top margin and clears color, leaves everything else along
 *                                              B) OR a full String that represents the full new styleLess the variant should have.
 *                                                * 'padding-top: 20px;' --> blows away any existing styles and sets top padding
 * @return {Object} {
 *                    lessPath: the path to the variant within the style block, e.g. [ 'medium', 'styleLess' ]
 *                    newLess: the variant's full new styleLess value -- will match options.newStyles if it's passed as a string
 *                    hasChanged: if true, indicates that the styleLess of the variant has changed
 *                    changedRuleGuid: e.g., 'some-block-guid:medium_hover' if the rule's CSS has changed, or null if it has not
 *                    oldValues: a map of previously-set values
 *                  }
 */
function rebuildStyleLess(
  styleBlock: StyleBlock,
  // @ts-expect-error - TS2741 - Property 'newStyles' is missing in type '{}' but required in type '{ variantKey?: string | undefined; newStyles: string | Record<any, any>; }'.
  {
    variantKey = 'main',
    newStyles,
    removeWithUnset = false,
  }: {
    variantKey?: string;
    newStyles: string | Record<any, any>;
    removeWithUnset?: UpdateStyleRuleProps['removeWithUnset'];
  } = {}
) {
  const variantPath = variantKey === 'main' ? [] : ['variants', variantKey];
  const lessPath = [...variantPath, 'styleLess'];
  const oldLess = styleBlock.getIn(lessPath);

  const futureProperties: Array<{
    property: string;
    value: string | number;
  }> = [];

  /**
   * A list of properties that went from being set to a normal value, to being
   * set to a custom (@raw) value.
   *
   * These properties are stored separately until the last moment to ensure that
   * they appear at the end of the property list, which ensures that when adding
   * a new custom property, its order doesn't "jump around".
   */
  const propertiesThatBecameCustom: typeof futureProperties = [];

  // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string'.
  const definedProperties = cssToPropertyList(oldLess);
  const fullReplaceMode = _.isString(newStyles);
  const oldValues: Record<string, any> = {};

  // If a replacement styleLess string is passed, need to convert from a string to a map
  // Make sure we clone the passed-in newStyles object so we don't mutate the passed reference
  // Shallow clone is sufficient, since we only have simple string -> string value pairs
  const newStylesMap =
    typeof newStyles === 'string'
      ? cssToPropertyMap(newStyles)
      : _.clone(newStyles);

  // Update previous properties
  // @ts-expect-error - TS7006 - Parameter 'pair' implicitly has an 'any' type.
  definedProperties.forEach((pair) => {
    const prop = pair.property;
    const oldValue = pair.value;

    if (_.has(newStylesMap, prop)) {
      const newValue = newStylesMap[prop];
      // If the old value is NOT custom, and the new value IS custom, we want
      // this prop to appear at the END of the properties list, treating it
      // effectively as if it's a new property.
      //
      // To accomplish this, we do _not_ add it to `futureProperties` yet, but
      // instead place it in a separate array which we append to
      // `futureProperties` after this loop completes.
      //
      // This ensures we maintain the order of properties from `newStyles`.
      if (
        (oldValue == null || !containsCssCustomValue(oldValue)) &&
        newValue != null &&
        containsCssCustomValue(newValue)
      ) {
        propertiesThatBecameCustom.push({property: prop, value: newValue});
      } else if (newValue !== null && newValue !== undefined) {
        // { propName: null|undefined } indicates that an existing CSS prop should be cleared
        futureProperties.push({property: prop, value: newValue});
      }

      // Store the old value for potential later reversal, but only if it has changed
      if (newValue !== oldValue) {
        oldValues[prop] = oldValue;
      }

      // eslint-disable-next-line webflow/no-delete
      delete newStylesMap[prop];
    } else {
      if (fullReplaceMode) {
        oldValues[prop] = oldValue;
      } else {
        futureProperties.push({property: prop, value: oldValue});
      }
    }
  });

  // Add any new ones
  for (const prop in newStylesMap) {
    const newValue = newStylesMap[prop];
    if (newValue !== null && newValue !== undefined) {
      futureProperties.push({property: prop, value: newValue});
      oldValues[prop] = null;
    }
  }

  // Finally add any newly-custom properties to the very end
  for (const {property, value} of propertiesThatBecameCustom) {
    futureProperties.push({property, value});
  }

  // Sort by logical property order, which makes individual property updates deterministic
  sortCssProperties(
    futureProperties,
    'property',
    (prop) =>
      typeof prop.value === 'string' && containsCssCustomValue(prop.value)
  );

  const newLess = futureProperties

    .map(({property, value}) => {
      const isUnsetValue =
        typeof value === 'string' &&
        (value === 'unset' || value === '@raw<|unset|>');
      if (removeWithUnset && isUnsetValue) {
        return '';
      }

      return property + ': ' + value + ';';
    })
    .join(' ');
  const hasChanged = newLess !== oldLess;
  const newVariant = oldLess === undefined && newLess && newLess !== '';
  const changedRuleGuid =
    hasChanged && !newVariant
      ? createStyleRuleGuid(styleBlock._id, variantKey)
      : null;

  return {lessPath, newLess, hasChanged, changedRuleGuid, oldValues};
}

export const forTestSuite = {
  ensureRawPasteOrder,
  rebuildStyleLess,
};

export interface UpdateStyleRuleProps {
  styleBlockGuid: string | null | undefined;
  mediaQuery?: BreakpointID | null;
  customNodeState?: CustomNodeStateKey | null;
  pseudoState?: PseudoStateKey | null;
  variantState?: VariantStateKey | null;
  newStyles: string | Record<any, any>;
  ephemeral?: boolean;
  neverEphemeral?: boolean;
  updateSpecFromRawPaste?: null | StyleUpdateSpec[];
  reifyIfFake?: boolean;
  /**
   * If true, any properties with the value 'unset' will be removed rather than set.
   * This was added to support the ability for the AI Assistant to remove
   * style properties by writing natural CSS values.
   *
   * @see https://webflow.atlassian.net/browse/GROW-5303
   * @see #proj-ai-webflow-assistant on Slack
   */
  removeWithUnset?: boolean;
}

/**
 * Updates specific properties of the StyleBlock's styleLess value.
 * @param  {String}         options.styleBlockGuid  The parent StyleBlock's guid.
 * @param  {String}         options.mediaQuery      The key/guid of the media query of the StyleRule being set.
 * @param  {STATES}         options.customNodeState    The Webflow specific state of this StyleRule, if any. e.g. "current", "open"
 * @param  {PSEUDOS}        options.pseudoState     The pseudo state of this rule, if any, e.g. "hover", "active", etc
 * @param  {Object|String}  options.newStyles       A) A map of discrete changes to apply on top of the existing variant styles
 *                                                    * { 'margin-top': '20px', 'color': null } --> adds/sets top margin and clears color, leaves everything else alone
 *                                                  B) OR a full String that represents the full new styleLess the variant should have.
 *                                                    * 'padding-top: 20px;' --> blows away any existing styles and sets top padding
 *
 * @param  {Boolean}  options.ephemeral       Set to true to indicate that this change should persist to CSSOM, but not the server via CssSync.
 * @param  {Boolean}  options.removeWithUnset  Set to true to indicate that this change should remove properties with the value 'unset' rather than setting them.
 */
export function updateStyleRule(
  styleBlockState: StyleBlockStateType,
  {
    styleBlockGuid,
    mediaQuery,
    customNodeState,
    pseudoState,
    variantState,
    newStyles,
    ephemeral = false,
    neverEphemeral = false,
    updateSpecFromRawPaste = null,
    reifyIfFake = false,
    removeWithUnset = false,
  }: UpdateStyleRuleProps
): StyleBlockStateType {
  if (styleBlockGuid == null) {
    return styleBlockState;
  }
  const block = styleBlockState.getBlock(styleBlockGuid);
  if (block == null || _.isEqual(newStyles, {})) {
    return styleBlockState;
  }
  const variantKey = createVariantKey({
    mediaQuery,
    customNodeState,
    pseudoState,
    variantState,
  });
  const styleRuleGuid = createStyleRuleGuid(styleBlockGuid, variantKey);
  const sameRuleChanging = styleBlockState.changedRule.guid === styleRuleGuid;
  const newlyEphemeral =
    ephemeral && (!styleBlockState.isInEphemeralState() || !sameRuleChanging);

  const {
    newLess: newLess_,
    hasChanged,
    changedRuleGuid,
    oldValues,
  } = rebuildStyleLess(block, {variantKey, newStyles, removeWithUnset});
  // Short circuit if there are no changes, otherwise changedRule.guid will reset to null
  // This is important in contantly-ephemeral situations, like drag-changing the margin/padding
  if (ephemeral && sameRuleChanging && !hasChanged) {
    return styleBlockState;
  }

  let newLess = newLess_;
  if (updateSpecFromRawPaste) {
    newLess = ensureRawPasteOrder(newLess, updateSpecFromRawPaste);
  }

  const oldBlock = styleBlockState.styleBlocks.get(styleBlockGuid);
  let newBlock = oldBlock;
  if (hasChanged) {
    newBlock =
      variantKey === BREAKPOINT_ID_MAIN
        ? oldBlock.set('styleLess', newLess)
        : // setIn uses a Map by default if no value exists. variants need to be an OrderedMap,
          // so we use updatedIn with a default OrderedMap here instead
          oldBlock.updateIn(
            ['variants', variantKey],
            (v: StyleBlockVariant = OrderedMap()) => v.set('styleLess', newLess)
          );
  }

  const oldCssPropertyMap = styleBlockState.cssPropertyMap.get(styleBlockGuid);
  const newCssPropertyMap = hasChanged
    ? getJustCssProperties(newBlock)
    : oldCssPropertyMap;

  const newState = styleBlockState
    .resetAddedRule()
    .update('changedRule', (changedRule) => {
      return changedRule
        .set('guid', changedRuleGuid)
        .set('ephemeral', ephemeral)
        .update('oldValues', (storedOldValues) => {
          return newlyEphemeral || neverEphemeral
            ? fromJS(oldValues)
            : storedOldValues;
        });
    })
    .setIn(['styleBlocks', styleBlockGuid], newBlock)
    .setIn(['cssPropertyMap', styleBlockGuid], newCssPropertyMap)
    .update('styleBlockIds', (styleBlockIds) =>
      styleBlockIds.has(styleBlockGuid)
        ? styleBlockIds
        : ImmSet([...styleBlockIds, styleBlockGuid])
    );

  return reifyIfFake && newBlock.fake
    ? reifyStyleBlock(newState, {_id: styleBlockGuid})
    : newState;
}
